import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { UserModel, CoreService, AuthenticationService } from 'src/app/services/api-services';
import { BaseComponent, DataTableRequest, EditComponent, EditSendRegistrationEmailComponent, LanguageService, OneColumnSearchData, SearchData, StringListItemModel, TranslatedToastrService, ValidationHandlerService } from 'lib/Vestra.shared';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { HttpErrorResponse } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { UserEditComponent } from '../user-edit/user-edit.component';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ADTSettings } from 'angular-datatables/src/models/settings';
import { Router } from '@angular/router';

@Component({
    selector: 'app-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.scss'],
})
export class UserListComponent extends BaseComponent implements OnInit, AfterViewInit {
    @ViewChild('actionsRef') actionsRef: TemplateRef<EditSendRegistrationEmailComponent>;
    public roles: StringListItemModel[];

    constructor(private router: Router,
         private coreService: CoreService, 
         private authenticationService: AuthenticationService,
         protected override validationHandlerService: ValidationHandlerService, 
         private translatedToastrService: TranslatedToastrService, protected override modalService: BsModalService, protected override spinnerService: NgxSpinnerService, protected translateService: TranslateService, protected override languageService: LanguageService, public datePipeInstance: DatePipe) {
        super(spinnerService, modalService, validationHandlerService, translateService, languageService);
    }

    public override getDtOptions() {
        this.languageUrl = this.languageService.getTranslationUrl();

        const res: ADTSettings = {
            serverSide: true,
            processing: false,
            pageLength: 20,
            lengthChange: false,
            responsive: {
                details: {
                    type: 'column',
                    target: -1,
                },
            },
            searching: false,
            destroy: true,
            language: {
                url: this.languageUrl,
            },
            ajax: (dataTablesParams: any, callback: any) => {
                const searchData: OneColumnSearchData = {
                    propertyValue: this.searchValue,
                };

                for (let order of dataTablesParams.order) {
                    order.name = dataTablesParams.columns[order.column]?.data;
                }

                const model: DataTableRequest = {
                    draw: dataTablesParams.draw,
                    start: dataTablesParams.start,
                    length: dataTablesParams.length,
                    order: dataTablesParams.order,
                    search: searchData,
                    columns: this.transformToColumnData(this.dtOptions.columns),
                };

                this.datatableRequest = model;

                this.handleSpinner(firstValueFrom(this.coreService.getUserListDataSource(model)))
                    .then(res => {
                        callback(this.getTableData(res));
                        return Promise.resolve(res);
                    })
                    .catch((err: HttpErrorResponse) => {
                        this.validationHandlerService.handleServerError(err);
                        callback(this.getEmptyTableData());
                    });
            },
            columns: [
                {
                    data: 'name',
                    title: 'user.name',
                    responsivePriority: 1,
                    searchable: true,
                    orderable: true,
                },
                {
                    data: 'email',
                    title: 'user.email',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'role',
                    title: 'user.role',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'partnerName',
                    title: 'partner.name',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'isActive',
                    title: 'user.is_active',
                    searchable: false,
                    orderable: true,
                    render: function (data: any, type: any, row: any, meta: any) {
                        const isChecked = data === true ? 'checked' : '';

                        return `
                            <input type="checkbox" class="form-check-input" disabled ${isChecked}>
                        `;
                    },
                },
                {
                    title: 'common.actions',
                    data: null,
                    searchable: false,
                    orderable: false,
                    defaultContent: '',
                    ngTemplateRef: {
                        ref: this.actionsRef,
                        context: {
                            captureEvents: this.onManageItem.bind(this),
                        },
                    },
                    responsivePriority: 1,
                },
                {
                    data: '',
                    orderable: false,
                    responsivePriority: 1,
                    render: function (data) {
                        return '';
                    },
                },
            ],
            columnDefs: [
                {
                    targets: '_all',
                    visible: true,
                },
                {
                    className: 'details-icon dtr-control',
                    orderable: false,
                    target: -1,
                },
            ],
        };

        return res;
    }

    public ngAfterViewInit() {
        this.dataTableAfterViewInit();
    }

    public ngOnDestroy() {
        this.dataTableOnDestroy();
    }

    ngOnInit() {
        this.dataTableOnInit();
        firstValueFrom(this.coreService.getRoles())
            .then(res => {
                this.roles = res.result;
            })
            .catch((err: HttpErrorResponse) => {
                this.validationHandlerService.handleServerError(err);
            });
    }

    public onManageItem(event: { action: string; data: any }) {
        if (event.action === 'edit') {
            this.openEditModalWithComponent(event.data);
        }
        else if(event.action == "sendRegistrationEmail")
        {
            this.openConfirmDialog(event.data.role + " -  " + event.data.name, () => this.onSendRegistrationEmail(event.data.email), null, 'common.send_registration_email_title', 'common.send_registration_email_message');
        }
    }
    public onSendRegistrationEmail(email: string)
    {
        this.handleSpinner(
            firstValueFrom(this.authenticationService.resendRegistrationEmail(email))
            .then(res => {
                return Promise.resolve(res);
            })
            .catch((err: HttpErrorResponse) => {
                this.validationHandlerService.handleServerError(err);
            })
        );
    }

    public onAddItem() {
        this.openEditModalWithComponent(null);
    }

    public openEditModalWithComponent(item: UserModel) {
        let modalOptions: ModalOptions = this.getDefaultModalOptions('user.user', item);
        modalOptions.initialState['roles'] = this.roles;

        let bsModalRef: BsModalRef = this.modalService.show(UserEditComponent, modalOptions);
        bsModalRef.onHide.subscribe((reason: string | any) => {
            if (reason === 'save_success') {
                this.translatedToastrService.success('common.save_success', 'common.success');
                this.refreshDataTable();
            }
        });
    }

    public onSearch(event: string): void {
        this.searchValue = event;
        this.refreshDataTable();
    }
}
